// Server state for data sources, the signal catalog and queries.
import type {
  BulkSignalsResponse,
  CreateDataSourceRequest,
  DataSource,
  DiscoveryResult,
  JobStatus,
  LastValue,
  OkResponse,
  QueryResult,
  QuerySpec,
  Signal,
  SignalCalc,
  SignalInput,
  TestConnectionResult,
  UpdateDataSourceRequest,
} from '@twinlink/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiFetch } from '@web/lib/api';

const base = (orgId: string) => `/orgs/${orgId}`;
export const dataKeys = {
  sources: (orgId: string) => ['org', orgId, 'datasources'] as const,
  signals: (orgId: string, filter?: object) => ['org', orgId, 'signals', filter ?? {}] as const,
  discovery: (orgId: string, id: string) => ['org', orgId, 'discovery', id] as const,
};

// ---- Data sources ----
export const useDataSources = (orgId: string, enabled = true) =>
  useQuery({ queryKey: dataKeys.sources(orgId), queryFn: () => api<{ items: DataSource[] }>(`${base(orgId)}/datasources`).then((r) => r.items), enabled });

export function useSaveDataSource(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id?: string; body: CreateDataSourceRequest | UpdateDataSourceRequest }) =>
      api<DataSource>(`${base(orgId)}/datasources${id ? `/${id}` : ''}`, { method: id ? 'PATCH' : 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: dataKeys.sources(orgId) }),
  });
}

export function useDeleteDataSource(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archiveSignals }: { id: string; archiveSignals: boolean }) =>
      api<OkResponse>(`${base(orgId)}/datasources/${id}`, { method: 'DELETE', query: { archiveSignals } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['org', orgId] }),
  });
}

export function useTestDataSource(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string } | { body: CreateDataSourceRequest }) =>
      'id' in input
        ? api<TestConnectionResult>(`${base(orgId)}/datasources/${input.id}/test`, { method: 'POST' })
        : api<TestConnectionResult>(`${base(orgId)}/datasources/test`, { method: 'POST', body: input.body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: dataKeys.sources(orgId) }),
  });
}

// ---- Discovery ----
export const useDiscoveryResult = (orgId: string, dataSourceId: string | null) =>
  useQuery({
    queryKey: dataKeys.discovery(orgId, dataSourceId ?? ''),
    queryFn: () => api<DiscoveryResult | null>(`${base(orgId)}/datasources/${dataSourceId}/discovery`),
    enabled: !!dataSourceId,
    staleTime: 60_000,
  });

/** Starts a discovery job and polls it until it finishes. Resolves with the job. */
export function useRunDiscovery(orgId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dataSourceId, lookbackDays, onProgress }: { dataSourceId: string; lookbackDays: number; onProgress?: (job: JobStatus) => void }) => {
      const { jobId } = await api<{ jobId: string }>(`${base(orgId)}/datasources/${dataSourceId}/discover`, { method: 'POST', body: { lookbackDays } });
      for (;;) {
        const job = await api<JobStatus>(`${base(orgId)}/jobs/${jobId}`);
        onProgress?.(job);
        if (job.status === 'completed') return job.result as DiscoveryResult;
        if (job.status === 'failed') throw new Error(job.error ?? 'Discovery failed');
        await new Promise((r) => setTimeout(r, 700));
      }
    },
    onSuccess: (result, vars) => qc.setQueryData(dataKeys.discovery(orgId, vars.dataSourceId), result),
  });
}

// ---- Signals ----
export interface SignalFilter {
  wellId?: string;
  fieldId?: string;
  dataSourceId?: string;
  quantity?: string;
  q?: string;
  status?: 'active' | 'archived' | 'all';
}
export const useSignals = (orgId: string, filter: SignalFilter = {}, enabled = true) =>
  useQuery({
    queryKey: dataKeys.signals(orgId, filter),
    queryFn: () => api<{ items: Signal[] }>(`${base(orgId)}/signals`, { query: { ...filter } }).then((r) => r.items),
    enabled,
    staleTime: 60_000,
  });

function useInvalidateSignals(orgId: string) {
  const qc = useQueryClient();
  return () => Promise.all([qc.invalidateQueries({ queryKey: ['org', orgId, 'signals'] }), qc.invalidateQueries({ queryKey: dataKeys.sources(orgId) })]);
}

export function useBulkCreateSignals(orgId: string) {
  const invalidate = useInvalidateSignals(orgId);
  return useMutation({
    mutationFn: (signals: SignalInput[]) => api<BulkSignalsResponse>(`${base(orgId)}/signals/bulk`, { method: 'POST', body: { signals } }),
    onSuccess: invalidate,
  });
}

export function useUpdateSignal(orgId: string) {
  const invalidate = useInvalidateSignals(orgId);
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: Partial<SignalInput> & { status?: 'active' | 'archived'; calc?: SignalCalc } }) =>
      api<Signal>(`${base(orgId)}/signals/${id}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
}

export function useDeleteSignal(orgId: string) {
  const invalidate = useInvalidateSignals(orgId);
  return useMutation({ mutationFn: (id: string) => api<OkResponse>(`${base(orgId)}/signals/${id}`, { method: 'DELETE' }), onSuccess: invalidate });
}

// ---- Queries ----
/**
 * Historical query. The previous result stays on screen while a new range loads, and live
 * ranges refetch on an interval — cheap, because the server only re-reads the newest chunk.
 */
export function useSeriesQuery(orgId: string, spec: QuerySpec | null, opts: { refetchMs?: number | false } = {}) {
  return useQuery({
    queryKey: ['org', orgId, 'query', spec],
    queryFn: ({ signal }) => api<QueryResult>(`${base(orgId)}/query`, { method: 'POST', body: spec, signal }),
    enabled: !!spec && spec.series.length > 0,
    placeholderData: keepPreviousData,
    refetchInterval: opts.refetchMs ?? false,
    staleTime: 2000,
    retry: false,
  });
}

export const useLastValues = (orgId: string, signalIds: string[], refetchMs: number | false = 5000) =>
  useQuery({
    queryKey: ['org', orgId, 'last', signalIds],
    queryFn: () => api<{ items: LastValue[] }>(`${base(orgId)}/query/last`, { method: 'POST', body: { signalIds } }).then((r) => new Map(r.items.map((i) => [i.signalId, i]))),
    enabled: signalIds.length > 0,
    refetchInterval: refetchMs,
    placeholderData: keepPreviousData,
    retry: false,
  });

/** Downloads an export file (CSV or XLSX) built by the server from the same QuerySpec. */
export async function downloadExport(orgId: string, spec: QuerySpec, format: 'csv' | 'xlsx'): Promise<void> {
  const res = await apiFetch(`${base(orgId)}/export`, { method: 'POST', body: { spec, format } });
  const name = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? `twinlink-export.${format}`;
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
